import { LinkButton, Stack, Typography } from '@compass/components'
import { capitalize } from '@mui/material/utils'
import clsx from 'clsx'
import { useToast } from 'hooks'
import { SearchIcon } from 'lucide-react'
import { ComponentProps, FC, ReactNode, useCallback } from 'react'

import { CopyButton } from './CopyButton'

type DetailRenderFunctionProps = {
  Label: FC<ComponentProps<typeof Typography>>
  Value: FC<ComponentProps<typeof Typography>>
  ValueRow: FC<{
    copyValue?: string
    onCopy?: () => void
    filterNoun?: string
    filterHref?: string
    onFilter?: () => void
    children?: ReactNode
  }>
}
export function SidebarDetail({
  children,
}: {
  children?: ReactNode | ((props: DetailRenderFunctionProps) => ReactNode)
}) {
  return (
    <Stack direction='column' gap={1} className='w-full'>
      {typeof children === 'function' ? children({ Label, Value, ValueRow }) : children}
    </Stack>
  )
}

function Label(props: ComponentProps<typeof Typography>) {
  return <Typography size='sm' className='text-gray-800' {...props} />
}
function Value(props: ComponentProps<typeof Typography>) {
  return <Typography size='base' className='text-gray-900' {...props} />
}
function ValueRow({
  copyValue,
  onCopy,
  filterNoun,
  filterHref,
  onFilter,
  children,
}: ComponentProps<DetailRenderFunctionProps['ValueRow']>) {
  const { showToast } = useToast()
  const onCopyToast = useCallback(() => {
    onCopy?.()
    showToast({
      severity: 'success',
      message: filterNoun ? `${capitalize(filterNoun)} copied to clipboard.` : 'Copied to clipboard.',
    })
  }, [filterNoun, onCopy, showToast])
  const onErrorToast = useCallback(() => {
    showToast({
      severity: 'error',
      message: filterNoun
        ? `Failed to copy the ${capitalize(filterNoun)} to clipboard.`
        : 'Failed to copy to clipboard.',
    })
  }, [filterNoun, showToast])

  return (
    <div className='group w-full relative p-2 -m-2'>
      {children}

      {copyValue || onFilter ? (
        <div
          className={clsx([
            'pointer-events-none',
            'absolute top-1 right-0',
            'opacity-0',
            'group-hover:opacity-100 group-hover:pointer-events-auto',
            'focus-within:opacity-100 focus-within:pointer-events-auto',
            'transition',
            'flex items-center gap-2',
          ])}
        >
          {copyValue ? (
            <CopyButton
              onCopy={onCopyToast}
              onError={onErrorToast}
              copyValue={copyValue}
              size='xs'
              tone='background2'
            />
          ) : null}
          {filterHref || onFilter ? (
            <LinkButton
              variant='secondary'
              tone='background2'
              size='xs'
              isIcon
              aria-label={`Filter events by this ${filterNoun ?? 'attribute'}`}
              href={filterHref}
              onPress={onFilter}
            >
              <SearchIcon />
            </LinkButton>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
